/**
 * Debounce function invocations. Mimics the behaviors of /pkg/debounce/debounce.go.
 *
 * @template T - The type of the function to debounce.
 * @param fn - The function to debounce.
 * @param waitTime - The number of milliseconds to delay. Default is 300ms.
 * @param maxWaitTime - The maximum number of milliseconds before the function is forcibly invoked. Default is 3000ms.
 * @returns A debounced version of the provided function.
 */
export const debounce = <T extends (...args: any[]) => void>( // eslint-disable-line @typescript-eslint/no-explicit-any
    fn: T,
    waitTime = 300,
    maxWaitTime = 3000,
): (...args: Parameters<T>) => void => {
    let lastCallTime: number | null = null
    let shortTimeout: ReturnType<typeof setTimeout> | null = null
    let maxTimeout: ReturnType<typeof setTimeout> | null = null
    let hasFiredFirstEvent = false
    let pendingArgs: Parameters<T> | null = null

    const clearShortTimer = (): void => {
        if (shortTimeout) {
            clearTimeout(shortTimeout)
            shortTimeout = null
        }
    }

    const clearMaxTimer = (): void => {
        if (maxTimeout) {
            clearTimeout(maxTimeout)
            maxTimeout = null
        }
    }

    const invokeFunction = (): void => {
        if (pendingArgs) {
            fn(...pendingArgs)
            pendingArgs = null
        }
        clearShortTimer()
        clearMaxTimer()
        lastCallTime = Date.now()
    }

    return (...args: Parameters<T>): void => {
        const now = Date.now()

        if (!hasFiredFirstEvent) {
            hasFiredFirstEvent = true
            fn(...args)
            lastCallTime = now
            return
        }

        pendingArgs = args

        if (lastCallTime === null || now - lastCallTime >= maxWaitTime) {
            invokeFunction()
            return
        }

        clearShortTimer()

        shortTimeout = setTimeout(invokeFunction, waitTime)

        if (!maxTimeout) {
            const maxWaitRemaining = maxWaitTime - (now - lastCallTime)
            maxTimeout = setTimeout(invokeFunction, maxWaitRemaining)
        }
    }
}
